<template>
  <div class="h-screen-safe flex min-h-0 flex-col">
    <HeaderBar class="md:hidden lg:block" :full-width="true" />

    <section class="mt-4 flex min-h-0 flex-1 flex-col">
      <div
        class="mb-4 flex flex-col justify-end gap-3 px-4 lg:flex-row-reverse lg:items-center lg:gap-4 lg:px-9"
      >
        <div class="flex items-center justify-start gap-4">
          <Input
            v-model="licencePlateFilter"
            :placeholder="t('filterCarLicencePlate')"
            type="text"
            class="w-full lg:w-60"
          />
          <CVButton
            size="iconSm"
            class="flex-shrink-0 lg:hidden"
            @click="showFilters = !showFilters"
          >
            <FunnelIcon v-if="!showFilters" class="size-4" />
            <FunnelXIcon v-else class="size-4" />
          </CVButton>
          <CVButton @click.prevent="jumpToToday"
            ><span class="hidden lg:inline">{{ t('jumpToToday') }}</span
            ><span class="lg:hidden">{{ t('today') }}</span></CVButton
          >
          <div class="relative hidden md:block lg:hidden">
            <HeaderBarHamburger v-model:isActive="isHamburgerActive" />
            <HeaderbarDropdown :class="{ hidden: !isHamburgerActive }" />
          </div>
        </div>
        <div
          class="flex-col justify-start gap-2 md:flex-row lg:flex lg:items-center lg:gap-4"
          :class="{
            hidden: !showFilters,
            flex: showFilters,
          }"
        >
          <Select v-model="makeFilter" :options="lineTypeOptions[0]" />
          <Select v-model="categoryFilter" :options="lineTypeOptions[1]" />
          <Select
            v-model="decommissionedFilter"
            :options="decommissionedOptions"
            empty-value-to="null"
          />
          <MultiSelect
            v-model="selectedExtras"
            :options="extrasFilterOptions"
            :placeholder="t('extras')"
            class="min-w-40"
          />
        </div>
      </div>
      <div class="flex min-h-0 flex-1 flex-col lg:px-9 lg:pb-9">
        <GanttChart
          id="bookings-gantt-chart"
          ref="ganttChart"
          class="h-full min-h-0 w-full flex-1"
          :categories="categories"
          :line-types="lineTypes"
          :lines="lines"
          :tags="tags"
          :blocks="blocks"
          @on-change-date="(newValue: DateTime) => (currentDate = newValue)"
          @on-calendar-click="onCalendarClick"
        />
        <GanttDropDown :options="carDropDownOptions" />
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { allocationsService } from '@/api/allocations.service.ts';
import CVButton from '@/components/buttons/CVButton.vue';
import HeaderbarDropdown from '@/components/headerbar/components/HeaderbarDropdown.vue';
import HeaderBarHamburger from '@/components/headerbar/components/HeaderBarHamburger.vue';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import Input from '@/components/Input.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import Select from '@/components/Select.vue';
import type { MinimalAllocationDto } from '@/entities/allocations/allocation.entity.ts';
import { BookingLocationType } from '@/entities/bookings/booking-location-type.enum';
import { BookingStatus } from '@/entities/bookings/booking-status.enum';
import type { CarStations } from '@/entities/cars/car.entity.ts';
import type { User } from '@/entities/auth/user.entity';
import { UserRole } from '@/entities/auth/user-role.enum';
import { DecommissionedFilter } from '@/entities/cars/decommissioned-filter.enum';
import { CarExtraFilter } from '@/entities/cars/car-extra-filter.enum';
import { FindAllCarsSort } from '@/entities/cars/find-all-cars-sort.enum';
import { Order } from '@/entities/pagination/order.enum';
import { getDateFormat, useDateTimeFormat } from '@/hooks/use-date-format';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { formatDate } from '@/hooks/use-formatted-date';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import { useCarsWithQuery } from '@/queries/use-cars';
import { useStationsWithQuery } from '@/queries/use-stations';
import { useActiveTimeTrackingEntries } from '@/queries/use-time-tracking';
import { useMe } from '@/queries/use-users';
import router from '@/router';
import { useUiStore } from '@/stores/ui.store';
import {
  AllocationStatus,
  AllocationType,
  FindAllAllocationsSort,
  type LocalizedFieldDto,
} from '@carvia/ros-client-types';
import { remove, truncate } from 'lodash';
import { FunnelIcon, FunnelXIcon } from 'lucide-vue-next';
import { DateTime } from 'luxon';
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch, watchEffect } from 'vue';
import { refDebounced, until, useIntervalFn } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';
import GanttChart from './components/GanttChart.vue';
import type { GanttDropDownOptions } from './components/GanttDropDown.vue';
import GanttDropDown from './components/GanttDropDown.vue';
import type {
  Block,
  Category,
  GanttHoverContent,
  GanttHoverRow,
  HideInterval,
  Line,
  LineInternal,
  LineType,
  LineTypeType,
  Tag,
} from './components/interfaces';
import { useAbility } from '@casl/vue';
import {
  availableAtBlocksForLine,
  getAllocationPlacementStationId,
  getMirrorStationRanges,
  getStationInfoLabel,
  hideIntervalsForStationLine,
  isFloatingBooking,
} from './gantt-car-stations';

const { t, locale } = useI18n();
const { getLocalizedField } = useLocalizedFields();
const uiStore = useUiStore();
const dateTimeFormat = useDateTimeFormat();

watchEffect(() => {
  uiStore.setHeaderTitle(t('bookingPlan'));
});

const ganttChart = ref();
const licencePlateFilter = useRouteQueryRef('licencePlate', '', 'string');
const debouncedLicencePlateFilter = refDebounced(licencePlateFilter, 300);
const makeFilter = useRouteQueryRef('makeFilter', 'make;*', 'string');
const categoryFilter = useRouteQueryRef('categoryFilter', 'category;*', 'string');
const decommissionedFilter = useRouteQueryRef<DecommissionedFilter | null>(
  'decommissioned',
  DecommissionedFilter.ACTIVE,
  'string',
  { nullQueryValue: 'ALL' },
);
const selectedExtras = useRouteQueryRef('extras', undefined, 'string', {
  isArray: true,
  emptyArrayTo: 'undefined',
});

const currentDate = ref<DateTime>();
const initialDate = ref<DateTime>();
const carDropDownOptions = ref<GanttDropDownOptions | null>(null);
const showFilters = ref(false);
const isHamburgerActive = ref(false);
const lastRefetch = ref<Date>();

let currentLeft: DateTime | null = null;
let currentRight: DateTime | null = null;

function fetchData(
  filter: {
    startDateBefore?: DateTime;
    startDateAfter?: DateTime;
    endDateBefore?: DateTime;
    endDateAfter?: DateTime;
  },
  replace: boolean = false,
) {
  allocationsService
    .getAllMinimal({
      filter: [
        {
          isActive: true,
          startDateBefore: filter.startDateBefore?.toJSDate() ?? undefined,
          startDateAfter: filter.startDateAfter?.toJSDate() ?? undefined,
          endDateBefore: filter.endDateBefore?.toJSDate() ?? undefined,
          endDateAfter: filter.endDateAfter?.toJSDate() ?? undefined,
        },
      ],
      sort: [FindAllAllocationsSort.START_DATE],
      limit: 10000,
      order: Order.ASC,
    })
    .then(async (res) => {
      await until(carsQuerySuccess).toBe(true);
      if (replace) {
        remove(blocks, (block) => block.tag !== TagSlug.NOT_AVAILABLE);
        remove(blocks, (block) => block.tag === TagSlug.BOOKED_AT);
      }
      res.items.forEach((allocation) => {
        blocks.push(allocationToBlock(allocation));
        blocks.push(...allocationMirrorBlocks(allocation));
      });
    });
}

useIntervalFn(() => {
  if (document.hasFocus()) {
    refetch(currentDate.value, undefined);
  }
}, 60_000);

function onWindowFocus() {
  if (lastRefetch.value && lastRefetch.value.getTime() < new Date().getTime() - 60_000) {
    refetch(currentDate.value, undefined);
  }
}

onMounted(() => {
  window.addEventListener('focus', onWindowFocus);
});
onUnmounted(() => {
  window.removeEventListener('focus', onWindowFocus);
});

const refetch = (newDate: DateTime | undefined, oldDate: DateTime | undefined) => {
  const left = newDate!.minus({ days: 50 });
  const right = newDate!.plus({ days: 50 });
  if (!oldDate) {
    lastRefetch.value = new Date();
    initialDate.value = newDate;
    fetchData(
      {
        endDateAfter: left,
        startDateBefore: right,
      },
      true,
    );
    currentLeft = left;
    currentRight = right;
  }
  if (left < currentLeft!) {
    const newCurrentLeft = left.minus({ days: 50 });
    fetchData({
      endDateAfter: newCurrentLeft,
      endDateBefore: currentLeft!,
    });
    currentLeft = newCurrentLeft;
  }
  if (right > currentRight!) {
    const newCurrentRight = right.plus({ days: 50 });
    fetchData({
      startDateAfter: currentRight!,
      startDateBefore: newCurrentRight,
    });
    currentRight = newCurrentRight;
  }
};

watch(currentDate, refetch);

const extrasFilterOptions = computed(() => [
  { label: t('sevenSeater'), value: CarExtraFilter.SEVEN_SEATER },
  { label: t('towHitch'), value: CarExtraFilter.TOW_HITCH },
]);

const ability = useAbility();
const canListUsers = computed(() => ability.can('list', 'User'));

const { data: hubActiveWorkTimers } = useActiveTimeTrackingEntries(
  computed(() => ({
    role: [UserRole.HUB_AGENT, UserRole.HUB_MANAGER, UserRole.ADMIN],
  })),
  canListUsers,
  { refetchInterval: 60_000 },
);

const hubClockedInUsersByStationId = computed(() => {
  const map = new Map<string, Map<string, User>>();
  if (!canListUsers.value) {
    return map;
  }
  for (const entry of hubActiveWorkTimers.value ?? []) {
    const user = entry.user;
    for (const st of user.stations ?? []) {
      if (!map.has(st.id)) {
        map.set(st.id, new Map());
      }
      map.get(st.id)!.set(user.id, user);
    }
  }
  return map;
});

const { data: stationsData } = useStationsWithQuery({
  limit: 10000,
  stationIds: (
    ability.relevantRuleFor('operateOn', 'Station')?.conditions?.['id'] as
      | { $in: string[] }
      | undefined
  )?.$in,
});

const stations = useFlattenPaginatedData(stationsData);
const { data: carsData, isSuccess: carsQuerySuccess } = useCarsWithQuery(
  computed(() => ({
    limit: 5000,
    sort: [FindAllCarsSort.MAKE, FindAllCarsSort.MODEL],
    decommissioned: decommissionedFilter.value ?? undefined,
    isGhostCar: false,
    hasTowHitch: selectedExtras.value?.includes(CarExtraFilter.TOW_HITCH) ? true : undefined,
    minSeats: selectedExtras.value?.includes(CarExtraFilter.SEVEN_SEATER) ? 7 : undefined,
  })),
);

const { data: user } = useMe();

const categories = computed<Category[]>(() => {
  locale.value;
  return stations.value.map((station) => {
    const usersById = hubClockedInUsersByStationId.value.get(station.id);
    const users = usersById?.size
      ? [...usersById.values()]
          .sort((a, b) =>
            `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`),
          )
          .map((u) => ({
            id: u.id,
            firstName: u.firstName,
            lastName: u.lastName,
            phone: u.phone,
          }))
      : undefined;
    return {
      id: station.id,
      title: getLocalizedField(station.info),
      hidden: !user.value?.stations.some((userStation) => userStation.id === station.id),
      users,
    };
  });
});

const cars = useFlattenPaginatedData(carsData);

const lineTypes = computed<LineType[]>(() => [
  {
    typeName: 'make',
    types: cars.value.reduce((acc, cur) => {
      if (!acc.find((type) => type.tag === cur.vehicleType?.make.id)) {
        acc.push({
          tag: cur.vehicleType?.make.id ?? '',
          title: cur.vehicleType?.make.name ?? '',
        });
      }
      return acc;
    }, [] as LineTypeType[]),
  },
  {
    typeName: 'category',
    types: cars.value.reduce((acc, cur) => {
      if (!acc.find((type) => type.tag === cur.vehicleType?.mainCategory)) {
        acc.push({
          tag: cur.vehicleType?.mainCategory ?? '',
          title: t(`categories.${cur.vehicleType?.mainCategory}`),
        });
      }
      return acc;
    }, [] as LineTypeType[]),
  },
]);

const lineTypeOptions = computed(() =>
  lineTypes.value.map((lineType) =>
    [{ title: t(lineType.typeName), tag: '*' }, ...lineType.types].map((type) => ({
      label: type.title,
      value: `${lineType.typeName};${type.tag}`,
    })),
  ),
);

const decommissionedOptions = computed(() => [
  {
    label: t('showAllCars'),
    value: null,
  },
  {
    label: t('hideDecommissionedCars'),
    value: DecommissionedFilter.ACTIVE,
  },
  {
    label: t('hideActiveCars'),
    value: DecommissionedFilter.DECOMMISSIONED,
  },
]);

function mergeHideIntervals(intervals: HideInterval[]): HideInterval[] {
  if (intervals.length === 0) return [];

  const sorted = [...intervals].sort(
    (a, b) => (a.hideFrom?.toMillis() ?? 0) - (b.hideFrom?.toMillis() ?? 0),
  );
  const merged: HideInterval[] = [];
  let current = { ...sorted[0] };

  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i];
    if (current.hideUntil && next.hideFrom && current.hideUntil.hasSame(next.hideFrom, 'day')) {
      current.hideUntil = next.hideUntil;
    } else {
      merged.push(current);
      current = { ...next };
    }
  }

  merged.push(current);
  return merged;
}

const lines = computed<Line[]>(() => {
  const res: Line[] = [];
  for (const car of cars.value) {
    const stations = new Map<string, CarStations & { fromDateTimestamp: number }>();

    for (const station of car.stations) {
      const fromDateTimestamp = station.fromDate ? new Date(station.fromDate).valueOf() : 0;
      if (
        !stations.has(station.station.id) ||
        stations.get(station.station.id)!.fromDateTimestamp < fromDateTimestamp
      ) {
        stations.set(station.station.id, { ...station, fromDateTimestamp });
      }
    }

    for (const station of stations.values()) {
      res.push({
        id: car.id + '#' + station.station.id,
        title: `${car.vehicleType?.make?.name ?? ''} ${car.vehicleType?.model ?? ''} (${car.licencePlate})`,
        category: station.station.id,
        tags: [car.licencePlate, car.vehicleType.make.name + ' ' + car.vehicleType.model],
        lineTypes: [
          {
            typeName: 'make',
            tag: car.vehicleType?.make.id ?? '',
          },
          {
            typeName: 'category',
            tag: car.vehicleType?.mainCategory ?? '',
          },
        ],
        visibleFrom: DateTime.fromISO(car.availableFrom),
        visibleUntil: car.availableUntil ? DateTime.fromISO(car.availableUntil) : null,
        hide: mergeHideIntervals(hideIntervalsForStationLine(car, station.station.id)),
        ...(car.notAtTheHub ? { color: '#fff0aa' } : {}),
      });
    }
  }
  return res;
});

const enum TagSlug {
  NOT_AVAILABLE = 'NOT_AVAILABLE',
  BOOKED_AT = 'BOOKED_AT',
  INSURANCE_CASE = 'INSURANCE_CASE',
}

const OVERDUE_BLOCK_COLOR = getComputedStyle(document.documentElement)
  .getPropertyValue('--color-card-danger-bg')
  .trim();
const OVERDUE_AFTER_HOURS = 12;

const tags = ref<Tag[]>([
  {
    slug: AllocationType.BOOKING,
    title: t('booking'),
    color: '#67a0e0',
  },
  {
    slug: BookingStatus.FLOATING,
    title: t('floating'),
    color: '#33cc33',
    alpha: 0.5,
    zIndex: 2,
  },
  {
    slug: AllocationType.BLOCKER,
    title: t('maintenance'),
    color: '#888',
  },
  {
    slug: AllocationType.PARTNER_BOOKING,
    title: t('partnerRent'),
    color: '#f90',
  },
  {
    slug: AllocationType.WORKSHOP_APPOINTMENT,
    title: t('workshopAppointment'),
    color: '#8b5cf6',
  },
  {
    slug: TagSlug.INSURANCE_CASE,
    title: t('insuranceCase'),
    color: '#ffb38c',
  },
  {
    slug: AllocationType.TRANSFER_DELIVERY,
    title: t('transferDelivery'),
    color: '#f9d300',
    zIndex: 1,
  },
  {
    slug: AllocationType.TRANSFER_RETURN,
    title: t('transferReturn'),
    color: '#f9d300',
    zIndex: 1,
  },
  {
    slug: TagSlug.NOT_AVAILABLE,
    title: t('notAvailable'),
    color: '#d5d5d5',
  },
  {
    slug: TagSlug.BOOKED_AT,
    title: t('bookedAtLegend'),
    color: '#d5d5d5',
  },
]);

const blocks = reactive<Block[]>([]);

watch(
  lines,
  () => {
    let availabilityBlockCount: number = 0;
    blocks.forEach((block, i) => {
      if (block.tag === TagSlug.NOT_AVAILABLE) {
        availabilityBlockCount = i;
      }
    });
    const newAvailabilityBlocks = [];
    for (const line of lines.value) {
      const car = cars.value.find((car) => car.id === line.id.split('#')[0]);
      const block = {
        lineId: line.id,
        tag: TagSlug.NOT_AVAILABLE,
      };
      car?.availableFrom &&
        newAvailabilityBlocks.push({
          ...block,
          id: 'availability-start-' + line.id,
          startDate: null,
          endDate: DateTime.fromISO(car.availableFrom).startOf('day'),
          title: `${t('availableFrom')} ${formatDate(car.availableFrom, getDateFormat())}`,
        });
      car?.availableUntil &&
        newAvailabilityBlocks.push({
          ...block,
          id: 'availability-end-' + line.id,
          startDate: DateTime.fromISO(car.availableUntil).endOf('day'),
          endDate: null,
          title: `${t('availableUntil')} ${formatDate(car.availableUntil, getDateFormat())}`,
        });
      if (car) {
        const lineStationId = line.id.split('#')[1];
        for (const { carStation, startDate, endDate } of availableAtBlocksForLine(
          car,
          lineStationId,
        )) {
          newAvailabilityBlocks.push({
            ...block,
            id: `available-at-${car.id}#${carStation.station.id}-${line.id}-${startDate?.toISO() ?? 'open'}-${endDate?.toISO() ?? 'open'}`,
            startDate: startDate ? startDate.startOf('day') : null,
            endDate: endDate ? endDate.startOf('day') : null,
            title: t('availableAt', {
              carStation: carStation.station.info[locale.value as keyof LocalizedFieldDto],
            }),
          });
        }
      }
    }
    blocks.splice(0, availabilityBlockCount, ...newAvailabilityBlocks);
  },
  { immediate: true },
);

const applyLineTypeFilters = () => {
  for (const filter of [makeFilter, categoryFilter]) {
    const [typeName, tag] = filter.value.split(';');
    ganttChart.value?.filterLinesByType(typeName, tag);
  }
};

watch(
  [makeFilter, categoryFilter, ganttChart, lines],
  async () => {
    await nextTick();
    applyLineTypeFilters();
  },
  { immediate: true },
);

watch(
  [debouncedLicencePlateFilter, ganttChart, lines],
  async ([value]) => {
    await nextTick();
    ganttChart.value?.filterLinesByTags(value);
  },
  { immediate: true },
);

function allocationToBlock(allocation: MinimalAllocationDto): Block {
  let startDate = DateTime.fromISO(allocation.startDate);
  let endDate = DateTime.fromISO(allocation.endDate);
  const tag = isFloatingBooking(allocation)
    ? BookingStatus.FLOATING
    : allocation.insuranceCaseId
      ? TagSlug.INSURANCE_CASE
      : allocation.type;
  const car = cars.value.find((c) => c.id === allocation.carId);
  const placementStationId = getAllocationPlacementStationId(allocation, car);
  const { title, showInHover } = getBlockTitle(allocation);
  return {
    lineId: allocation.carId + '#' + placementStationId,
    id: allocation.id,
    tag,
    title,
    onClick: () => router.push(getPushTarget(allocation)),
    startDate,
    endDate,
    hover: () => getHover(allocation, showInHover ? title : undefined),
    transferOfId: allocation.transferOfId,
    color: getAllocationOverdue(allocation) ? OVERDUE_BLOCK_COLOR : undefined,
  };
}

function allocationMirrorBlocks(allocation: MinimalAllocationDto): Block[] {
  const car = cars.value.find((c) => c.id === allocation.carId);
  if (!car) return [];
  const placementStationId = getAllocationPlacementStationId(allocation, car);
  const bookedAtLabel = getStationInfoLabel(car, placementStationId, locale.value);
  const isFloating = isFloatingBooking(allocation);
  const isCustomerBooking =
    allocation.type === AllocationType.BOOKING ||
    allocation.type === AllocationType.PARTNER_BOOKING;
  const block = getBlockTitle(allocation);
  const { title, showInHover } = isFloating
    ? { title: t('floatingAt', { carStation: bookedAtLabel }), showInHover: true }
    : isCustomerBooking
      ? { title: t('bookedAt', { carStation: bookedAtLabel }), showInHover: true }
      : block;
  return getMirrorStationRanges(allocation, car, placementStationId).map(
    ({ stationId, startDate, endDate }) => ({
      lineId: allocation.carId + '#' + stationId,
      id: `booked-at-${allocation.id}-${stationId}`,
      tag: isFloating ? BookingStatus.FLOATING : TagSlug.BOOKED_AT,
      title,
      startDate,
      endDate,
      onClick: () => router.push(getPushTarget(allocation)),
      hover: () => getHover(allocation, showInHover ? title : undefined),
    }),
  );
}

function getAllocationOverdue(allocation: MinimalAllocationDto) {
  if (allocation.type !== AllocationType.BOOKING) return;
  const now = DateTime.now();
  if (allocation.status === AllocationStatus.CONFIRMED) {
    const dueAt = DateTime.fromISO(allocation.startDate);
    if (now.diff(dueAt, 'hours').hours >= OVERDUE_AFTER_HOURS) {
      return { kind: 'handover' as const, dueAt };
    }
  }
  if (allocation.status === AllocationStatus.HANDED_OVER) {
    const dueAt = DateTime.fromISO(allocation.endDate);
    if (now.diff(dueAt, 'hours').hours >= OVERDUE_AFTER_HOURS) {
      return { kind: 'return' as const, dueAt };
    }
  }
}

function formatOverdueDuration(dueAt: DateTime) {
  const totalHours = Math.round(DateTime.now().diff(dueAt).as('hours'));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const parts = [
    ...(days ? [t('overdueDays', days)] : []),
    ...(hours || !days ? [t('overdueHours', hours)] : []),
  ];
  return parts.join(` ${t('overdueAnd')} `);
}

function hoverExtras(allocation: MinimalAllocationDto) {
  const badges: NonNullable<GanttHoverContent['badges']> = [];
  let allocationStatus: AllocationStatus | undefined;
  if (
    allocation.type === AllocationType.BOOKING &&
    allocation.bookingStatus !== BookingStatus.FLOATING
  ) {
    allocationStatus = allocation.status;
  }
  if (allocation.type === AllocationType.WORKSHOP_APPOINTMENT) {
    allocationStatus = allocation.status;
    if (allocation.isBlocking === false) {
      badges.push({ variant: 'warning', text: t('doesNotBlockBookings') });
    }
  }
  const overdue = getAllocationOverdue(allocation);
  if (overdue) {
    badges.push({
      variant: 'danger',
      text: t(overdue.kind === 'handover' ? 'handoverOverdue' : 'returnOverdue', {
        overdue: formatOverdueDuration(overdue.dueAt),
      }),
    });
  }
  return { allocationStatus, badges: badges.length ? badges : undefined };
}

function getHover(allocation: MinimalAllocationDto, subtitle?: string): GanttHoverContent {
  const start = DateTime.fromISO(allocation.startDate).toFormat(dateTimeFormat.value);
  const end = DateTime.fromISO(allocation.endDate).toFormat(dateTimeFormat.value);
  const car = cars.value.find((c) => c.id === allocation.carId);
  const rows: GanttHoverRow[] = [];

  if (subtitle) {
    rows.push({ text: subtitle });
  }

  const pushStartEnd = () => {
    rows.push({ icon: 'start', text: start });
    rows.push({ icon: 'end', text: end });
  };

  switch (allocation.type) {
    case AllocationType.BOOKING: {
      if (allocation.bookingNumber != null) {
        rows.push({ icon: 'hash', text: String(allocation.bookingNumber) });
      }
      if (allocation.customerName) rows.push({ icon: 'user', text: allocation.customerName });
      const delivery = displayLocation(allocation, 'pickup');
      const returnLocation = displayLocation(allocation, 'dropoff');
      rows.push({ icon: 'start', text: start });
      if (delivery) rows.push({ icon: 'pin', text: delivery });
      rows.push({ icon: 'end', text: end });
      if (returnLocation) rows.push({ icon: 'pin', text: returnLocation });
      break;
    }
    case AllocationType.TRANSFER_DELIVERY:
    case AllocationType.TRANSFER_RETURN: {
      if (allocation.driverName) rows.push({ icon: 'user', text: allocation.driverName });
      pushStartEnd();
      const locationInfo = displayLocation(
        allocation,
        allocation.type === AllocationType.TRANSFER_DELIVERY ? 'dropoff' : 'pickup',
      );
      if (locationInfo) rows.push({ icon: 'pin', text: locationInfo });
      break;
    }
    case AllocationType.WORKSHOP_APPOINTMENT: {
      const workshopTypes =
        allocation.workshopAppointmentTypes
          ?.map((type) => t(`workshopAppointmentTypes.${type}`))
          .join(', ') ?? '';
      if (workshopTypes) rows.push({ icon: 'wrench', text: workshopTypes });
      pushStartEnd();
      break;
    }
    case AllocationType.PARTNER_BOOKING: {
      if (allocation.comment) rows.push({ icon: 'hash', text: allocation.comment });
      pushStartEnd();
      break;
    }
    case AllocationType.BLOCKER: {
      pushStartEnd();
      break;
    }
  }

  return {
    title: allocation.carName ?? '',
    licencePlate: car?.licencePlate,
    rows,
    ...hoverExtras(allocation),
  };
}

const displayLocation = (allocation: MinimalAllocationDto, direction: 'pickup' | 'dropoff') => {
  if (allocation[`${direction}LocationType`] === BookingLocationType.STATION) {
    if (allocation[`${direction}ResponsibleStationId`] !== allocation.stationId) {
      return getLocalizedField(allocation[`${direction}ResponsibleStationInfo`]);
    }
  } else {
    return truncate(allocation[`${direction}LocationGeocodedAddress`]?.displayName, {
      length: 30,
      omission: '…',
    });
  }
  return null;
};

const getPushTarget = (allocation: MinimalAllocationDto) => {
  switch (allocation.type) {
    case AllocationType.BOOKING:
      return { name: 'bookingPlanEdit', params: { id: allocation.bookingId } };
    case AllocationType.BLOCKER:
      return {
        name: 'maintenanceEdit',
        params: { carId: allocation.carId, blockId: allocation.id },
      };
    case AllocationType.PARTNER_BOOKING:
      return {
        name: 'partnerRentEdit',
        params: { carId: allocation.carId, bookingId: allocation.id },
      };
    case AllocationType.TRANSFER_DELIVERY:
    case AllocationType.TRANSFER_RETURN:
      return {
        name: 'transferEdit',
        params: {
          transferType: allocation.type,
          allocationId: allocation.transferOfId,
          transferId: allocation.id,
        },
      };
    case AllocationType.WORKSHOP_APPOINTMENT:
      return {
        name: 'editWorkshopAppointment',
        params: { id: allocation.workshopAppointmentId ?? allocation.id },
      };
    default:
      return {};
  }
};

const getBlockTitle = (
  allocation: MinimalAllocationDto,
): { title: string; showInHover: boolean } => {
  switch (allocation.type) {
    case AllocationType.BOOKING:
      return { title: `${allocation.customerName}`, showInHover: false };
    case AllocationType.TRANSFER_DELIVERY:
    case AllocationType.TRANSFER_RETURN:
      return { title: `${allocation.driverName}`, showInHover: false };
    case AllocationType.WORKSHOP_APPOINTMENT:
      return { title: t('workshopAppointment'), showInHover: true };
    case AllocationType.BLOCKER:
      return { title: allocation.comment ?? t('maintenance'), showInHover: true };
    case AllocationType.PARTNER_BOOKING:
      return {
        title: t(`rentalPartners.${allocation.rentalPartner}`) + ` (${allocation.comment})`,
        showInHover: true,
      };
    default:
      return { title: allocation.bookingNumber?.toString() ?? '-', showInHover: true };
  }
};

const jumpToToday = () => {
  ganttChart.value.jumpToToday();
};

const onCalendarClick = (x: number, y: number, line: LineInternal | undefined, date: DateTime) => {
  carDropDownOptions.value = {
    show: !carDropDownOptions.value?.show,
    position: { x: Math.round(x), y: Math.round(y) },
    items: [
      ...(line
        ? [
            {
              label: t('createBlock'),
              onClick: () =>
                router.push({
                  name: 'maintenanceEdit',
                  params: { carId: line.id.split('#')[0], blockId: 'new' },
                  query: { startDate: date.toISO() },
                }),
            },
          ]
        : []),
    ],
  };
  if (!carDropDownOptions.value?.items?.length) carDropDownOptions.value.show = false;
};
</script>

<i18n lang="json">
{
  "en": {
    "bookingPlan": "Booking Plan",
    "filterCarLicencePlate": "Filter car/license plate",
    "booking": "Booking",
    "maintenance": "Maintenance/Cleaning",
    "partnerRent": "Partner Rent",
    "make": "Make (All)",
    "category": "Category (All)",
    "jumpToToday": "Jump to today",
    "today": "Today",
    "availableAt": "Available at {carStation}",
    "bookedAt": "Booked at {carStation}",
    "bookedAtLegend": "Booked at other station",
    "floatingAt": "Floating at {carStation}",
    "availableFrom": "Available from",
    "availableUntil": "Available until",
    "notAvailable": "Not available",
    "showAllCars": "Show all cars",
    "hideDecommissionedCars": "Hide decommissioned cars",
    "hideActiveCars": "Hide active cars",
    "startTime": "Start time",
    "endTime": "End time",
    "bookingNumber": "Bnr.",
    "deliveryTransfer": "Delivery Transfer",
    "returnTransfer": "Return Transfer",
    "location": "Location",
    "driver": "Driver",
    "customer": "Customer",
    "reservationNr": "Reservation nr.",
    "createBlock": "+ Create blocking",
    "insuranceCase": "Insurance case",
    "workshopAppointment": "Workshop appointment",
    "workshopAppointmentTypesLabel": "Workshop appointment",
    "showFilters": "Show filters",
    "hideFilters": "Hide filters",
    "extras": "Extras",
    "sevenSeater": "7-seater",
    "towHitch": "Tow hitch",
    "doesNotBlockBookings": "Does not block bookings",
    "overdueDays": "{n} day | {n} days",
    "overdueHours": "{n} hour | {n} hours",
    "overdueAnd": "and",
    "handoverOverdue": "Handover overdue by {overdue}",
    "returnOverdue": "Return overdue by {overdue}"
  },
  "de": {
    "bookingPlan": "Belegungsplan",
    "filterCarLicencePlate": "Filter nach Auto/Kennzeichen",
    "booking": "Buchung",
    "maintenance": "Wartung/Reinigung",
    "partnerRent": "Partnermiete",
    "make": "Marke (Alle)",
    "category": "Kategorie (Alle)",
    "jumpToToday": "Zu Heute springen",
    "today": "Heute",
    "availableAt": "Verfügbar in {carStation}",
    "bookedAt": "Gebucht in {carStation}",
    "bookedAtLegend": "Gebucht an anderer Station",
    "floatingAt": "Schwebend in {carStation}",
    "availableFrom": "Verfügbar ab",
    "availableUntil": "Verfügbar bis",
    "notAvailable": "Nicht verfügbar",
    "showAllCars": "Alle Autos anzeigen",
    "hideDecommissionedCars": "Ausrangierte Autos ausblenden",
    "hideActiveCars": "Aktive Autos ausblenden",
    "startTime": "Startzeit",
    "endTime": "Endzeit",
    "bookingNumber": "Bnr.",
    "deliveryTransfer": "Anlieferung",
    "returnTransfer": "Rückführung",
    "location": "Ort",
    "driver": "Fahrer",
    "customer": "Kunde",
    "reservationNr": "Reservierungsnr.",
    "createBlock": "+ Blockierung erstellen",
    "insuranceCase": "Versicherungsfall",
    "workshopAppointment": "Werkstatttermin",
    "workshopAppointmentTypesLabel": "Werkstatttermin",
    "showFilters": "Filter anzeigen",
    "hideFilters": "Filter verstecken",
    "extras": "Extras",
    "sevenSeater": "7-Sitzer",
    "towHitch": "Anhängerkupplung",
    "doesNotBlockBookings": "Blockiert keine Buchungen",
    "overdueDays": "{n} Tag | {n} Tagen",
    "overdueHours": "{n} Stunde | {n} Stunden",
    "overdueAnd": "und",
    "handoverOverdue": "Übergabe überfällig seit {overdue}",
    "returnOverdue": "Rückgabe überfällig seit {overdue}"
  },
  "es": {
    "bookingPlan": "Plan de ocupación",
    "filterCarLicencePlate": "Filtrar coche / matrícula",
    "booking": "Reserva",
    "maintenance": "Mantenimiento / limpieza",
    "partnerRent": "Alquiler partner",
    "make": "Marca (todas)",
    "category": "Categoría (todas)",
    "jumpToToday": "Ir a hoy",
    "today": "Hoy",
    "availableAt": "Disponible en {carStation}",
    "bookedAt": "Reservado en {carStation}",
    "bookedAtLegend": "Reservado en otra estación",
    "floatingAt": "Pendiente en {carStation}",
    "availableFrom": "Disponible desde",
    "availableUntil": "Disponible hasta",
    "notAvailable": "No disponible",
    "showAllCars": "Mostrar todos los coches",
    "hideDecommissionedCars": "Ocultar coches dados de baja",
    "hideActiveCars": "Ocultar coches activos",
    "startTime": "Hora de inicio",
    "endTime": "Hora de fin",
    "bookingNumber": "N.º res.",
    "deliveryTransfer": "Traslado de entrega",
    "returnTransfer": "Traslado de recogida",
    "location": "Ubicación",
    "driver": "Conductor",
    "customer": "Cliente",
    "reservationNr": "N.º reserva",
    "createBlock": "+ Crear bloqueo",
    "insuranceCase": "Siniestro",
    "workshopAppointment": "Cita de taller",
    "workshopAppointmentTypesLabel": "Cita de taller",
    "showFilters": "Mostrar filtros",
    "hideFilters": "Ocultar filtros",
    "extras": "Extras",
    "sevenSeater": "7 plazas",
    "towHitch": "Enganche de remolque",
    "doesNotBlockBookings": "No bloquea reservas",
    "overdueDays": "{n} día | {n} días",
    "overdueHours": "{n} hora | {n} horas",
    "overdueAnd": "y",
    "handoverOverdue": "Entrega atrasada desde hace {overdue}",
    "returnOverdue": "Devolución atrasada desde hace {overdue}"
  }
}
</i18n>
